/**
 * El panel: lo que hay que mirar hoy.
 *
 * Tres bloques y en este orden: dinero, cosas que esperan una decisión, y lo
 * que está pasando. Los avisos de arriba son accionables — cada uno lleva al
 * lugar donde se resuelve — porque un contador sin acción es ruido.
 */

import { Link } from 'react-router-dom';
import { periodLabel } from '@cleanblue/shared';
import { useApi } from '@/lib/hooks';
import {
  Async, Card, CardSkeleton, Empty, Money, Note, PageHead, Stat, Table, TableSkeleton,
} from '@/components/ui';

interface Dashboard {
  period: string;
  money: { outstanding: string; collected: string; billed_this_period: string };
  counts: {
    charges_this_period: number;
    submissions_pending: number;
    charges_needing_review: number;
    overdue: number;
    missing_tax_id: number;
    unconfirmed_allocations: number;
    units_total: number;
    units_active: number;
    units_prospect: number;
    /** Lo propio de la empresa: planilla, impuestos, celular, alquiler (0019). */
    obligations_due_week: number;
    obligations_overdue: number;
  };
  open_requests: { id: string; category: string; note: string | null; unit_code: string | null; created_at: string }[];
  recent_payments: {
    id: string; amount_minor: number; paid_at: string; method: string;
    billing_name_snapshot: string; unit_code: string;
  }[];
}

export function AdminDashboard() {
  const query = useApi<Dashboard>('/admin/dashboard');

  return (
    <Async
      query={query}
      // Forma aproximada del panel: las 4 cifras de arriba y las dos tablas
      // de abajo (3 columnas cada una, como sus `head` reales) — es un solo
      // `Async` para toda la pantalla, así que el esqueleto tiene que cubrir
      // el bloque entero en vez de una tabla sola.
      skeleton={
        <>
          <div className="grid grid--4">
            {Array.from({ length: 4 }, (_, i) => <CardSkeleton key={i} lines={2} />)}
          </div>
          <div className="grid grid--2 mt-3">
            <TableSkeleton rows={4} cols={3} />
            <TableSkeleton rows={4} cols={3} />
          </div>
        </>
      }
    >
      {(data) => (
        <>
          <PageHead
            title="Panel"
            subtitle={`Período en curso: ${periodLabel(data.period)}`}
            actions={<Link to="/admin/cobros/generar" className="btn btn--primary">Generar el mes</Link>}
          />

          <div className="grid grid--4">
            <Stat
              accent
              icon="por-cobrar"
              label="Por cobrar"
              value={<Money value={data.money.outstanding} size="lg" />}
              meta={data.counts.overdue > 0
                ? <Link to="/admin/cobros/pagos">{data.counts.overdue} vencidos</Link>
                : 'Nada vencido'}
            />
            <Stat
              icon="cobrado"
              label="Cobrado"
              value={<Money value={data.money.collected} size="lg" />}
              meta="Histórico confirmado"
            />
            <Stat
              icon="facturado"
              label="Facturado este mes"
              value={<Money value={data.money.billed_this_period} size="lg" />}
              meta={`${data.counts.charges_this_period} cobros emitidos`}
            />
            <Stat
              icon="consultorio"
              label="Consultorios"
              value={data.counts.units_active}
              meta={`${data.counts.units_prospect} prospectos · ${data.counts.units_total} en total`}
            />
          </div>

          {/* Lo que la empresa PAGA, no lo que cobra. Es la mitad del mes que
              el panel no mostraba: planilla, impuestos, el celular de atención. */}
          <div className="grid grid--2 mt-3">
            <Stat
              accent={data.counts.obligations_overdue > 0}
              icon="obligaciones"
              label="Obligaciones esta semana"
              value={data.counts.obligations_due_week}
              meta={<Link to="/admin/obligaciones">Ver la agenda</Link>}
            />
            <Stat
              icon="alerta"
              label="Obligaciones vencidas"
              value={data.counts.obligations_overdue}
              meta={data.counts.obligations_overdue === 0
                ? 'Nada atrasado'
                : <Link to="/admin/obligaciones">Ponerse al día</Link>}
            />
          </div>

          <Attention counts={data.counts} />

          <div className="grid grid--2 mt-3">
            <Card title="Solicitudes abiertas" flush>
              {data.open_requests.length === 0 ? (
                <Empty icon="confirmar" title="Ninguna solicitud pendiente" hint="Todo atendido." />
              ) : (
                <Table head={<tr><th>Consultorio</th><th>Solicitud</th><th className="right">Cuándo</th></tr>}>
                  {data.open_requests.map((r) => (
                    <tr key={r.id}>
                      <td className="strong">{r.unit_code ?? '—'}</td>
                      <td>
                        {r.category}
                        {r.note && <span className="tbl__sub">{r.note}</span>}
                      </td>
                      <td className="right small muted">{r.created_at.slice(0, 10)}</td>
                    </tr>
                  ))}
                </Table>
              )}
            </Card>

            <Card title="Últimos pagos" flush>
              {data.recent_payments.length === 0 ? (
                <Empty icon="cobros" title="Todavía no hay pagos" hint="Van a aparecer acá al confirmarlos." />
              ) : (
                <Table head={<tr><th>Pagador</th><th>Consultorio</th><th className="right">Monto</th></tr>}>
                  {data.recent_payments.map((p) => (
                    <tr key={p.id}>
                      <td>
                        {p.billing_name_snapshot}
                        <span className="tbl__sub">{p.paid_at.slice(0, 10)}</span>
                      </td>
                      <td>{p.unit_code}</td>
                      <td className="right"><Money value={p.amount_minor} /></td>
                    </tr>
                  ))}
                </Table>
              )}
            </Card>
          </div>
        </>
      )}
    </Async>
  );
}

/**
 * Lo que espera una decisión humana.
 *
 * Cada aviso enlaza a donde se resuelve. Si no hay nada pendiente, el bloque
 * desaparece en vez de mostrar cuatro ceros.
 */
function Attention({ counts }: { counts: Dashboard['counts'] }) {
  const items = [
    counts.submissions_pending > 0 && {
      tone: 'warn' as const,
      to: '/admin/cobros/pagos',
      text: <><strong>{counts.submissions_pending}</strong> comprobante{counts.submissions_pending === 1 ? '' : 's'} esperando revisión</>,
    },
    counts.overdue > 0 && {
      tone: 'danger' as const,
      to: '/admin/cobros/pagos',
      text: <><strong>{counts.overdue}</strong> cobro{counts.overdue === 1 ? '' : 's'} vencido{counts.overdue === 1 ? '' : 's'}</>,
    },
    counts.charges_needing_review > 0 && {
      tone: 'warn' as const,
      to: '/admin/cobros/conciliacion',
      text: <><strong>{counts.charges_needing_review}</strong> mes{counts.charges_needing_review === 1 ? '' : 'es'} del histórico sin resolver</>,
    },
    counts.unconfirmed_allocations > 0 && {
      tone: 'info' as const,
      to: '/admin/contratos',
      text: <><strong>{counts.unconfirmed_allocations}</strong> repartos sin confirmar: se está asumiendo partes iguales</>,
    },
    counts.obligations_overdue > 0 && {
      tone: 'danger' as const,
      to: '/admin/obligaciones',
      text: <><strong>{counts.obligations_overdue}</strong> obligación{counts.obligations_overdue === 1 ? '' : 'es'} de la empresa sin pagar y ya vencida{counts.obligations_overdue === 1 ? '' : 's'}</>,
    },
    counts.missing_tax_id > 0 && {
      tone: 'info' as const,
      to: '/admin/clientes?tab=doctores',
      text: <><strong>{counts.missing_tax_id}</strong> pagadores sin cédula: no se les puede facturar</>,
    },
  ].filter(Boolean) as { tone: 'warn' | 'danger' | 'info'; to: string; text: React.ReactNode }[];

  if (items.length === 0) {
    return (
      <div className="mt-3">
        <Note tone="ok">Nada pendiente de revisión. Todo al día.</Note>
      </div>
    );
  }

  return (
    <div className="mt-3" style={{ display: 'grid', gap: 8 }}>
      {items.map((item) => (
        <Note key={item.to + String(item.tone)} tone={item.tone}>
          {item.text} · <Link to={item.to}>Resolver</Link>
        </Note>
      ))}
    </div>
  );
}
